import React, { useCallback } from "react";

import { FullFormState } from "../../../../Engine/Core/Store/FullFormState";
import { isNotEmptyFilters } from "../../../../Helpers/FilteringHelpers";
import { currentViewModelSelect } from "../../../../Helpers/StateAccessors/ViewModelSelect";
import { PathResolver, withResolvePaths } from "../../../withResolvePaths";
import { AbsoluteModelPath } from "../../../../../../Common/ModelPath/ModelPath";
import { useTypedDispatch, useTypedSelector } from "../../../../../../Common/UseTypedSelector";
import { useMultilineClient } from "../../../../Engine/Core/Filtering/UseMultilineClient";
import { createHocDisplayName } from "../../../TypingUtils";

import { FilterInfo, FilterInfoProps } from "./FilterInfo";

type FilterInfoContainerProps = FilterInfoProps & {
    multilinePath: AbsoluteModelPath;
    resolvePath: PathResolver;
    filteredUniqKey?: string;
};

const FilterInfoContainerWrapper: React.FC<FilterInfoContainerProps> = ownProps => {
    const { multilinePath, resolvePath, filteredUniqKey = "default" } = ownProps;
    const dispatch = useTypedDispatch();

    const resolvedMultilinePath = resolvePath(multilinePath);
    const { client, filters, displayedInstances, filteringResultVersion } = useMultilineClient(
        resolvedMultilinePath,
        filteredUniqKey,
        info => ({
            client: info.client,
            displayedInstances: info.multilineState.displayedInstances,
            filters: info.multilineState.filters,
            filteringResultVersion: info.multilineState.filteringResultVersion,
        })
    );
    const filtered = isNotEmptyFilters(filters);
    const foundInstancesCount = filtered ? displayedInstances.length : 0;

    const instancesCount = useTypedSelector((state: FullFormState) => {
        const children = currentViewModelSelect.children(state, resolvedMultilinePath, []);
        return children.length;
    });

    const handleResetFilters = useCallback(() => {
        client.removeAllFilters();
    }, [dispatch, resolvedMultilinePath, resolvePath, filteredUniqKey]);

    return (
        <FilterInfo
            {...ownProps}
            filters={filters}
            foundInstancesCount={foundInstancesCount}
            instancesCount={instancesCount}
            filteringResultVersion={filteringResultVersion}
            filtered={filtered}
            resetFilters={handleResetFilters}
        />
    );
};
FilterInfoContainerWrapper.displayName = createHocDisplayName(FilterInfo, "FilterInfoContainerWrapper");

export const FilterInfoContainer = withResolvePaths(FilterInfoContainerWrapper);
